/* ============================================================
   SIDEBAR — Цифровой ДИАК ЗФ
   Design: Арктический Командный Центр — тёмный сайдбар,
   светлый основной контент. PT Sans, Норникель синий.
   Ширина: --app-sidebar-width (15rem | 4.5rem), см. SidebarLayoutContext.
   ============================================================ */

import { useMemo } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/contexts/ProjectContext";
import { useSidebarLayout } from "@/contexts/SidebarLayoutContext";
import { passportToEditable, compareSidebarCoreProjects } from "@/store/projectStore";
import {
  LayoutDashboard,
  History,
  ClipboardCheck,
  Search,
  BarChart3,
  FileText,
  Package,
  FileSignature,
  Calculator,
  TrendingUp,
  LineChart,
  FolderOpen,
  Users,
  Building2,
  ChevronRight,
  ChevronLeft,
  Bot,
  LogOut,
  UserCog,
  ScrollText,
  RotateCcw,
} from "lucide-react";
import { GANTT_ITEMS, PROJECTS } from "@/data/projects";
import { filterProjectsByMainGantt } from "@/lib/mainGanttProjectSpan";
import { cn } from "@/lib/utils";
import { NotificationBell } from "./NotificationBell";
import { Inbox } from "lucide-react";
import { useNotifications } from "@/contexts/NotificationsContext";

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  ClipboardCheck,
  Search,
  BarChart3,
  FileText,
  Package,
  FileSignature,
  Calculator,
  TrendingUp,
  LineChart,
  FolderOpen,
  Users,
  Building2,
};

const statusColors: Record<string, string> = {
  active: "#00A651",
  pilot: "#F5A623",
  planned: "#4A90D9",
  done: "#8E9BAE",
};

export default function Sidebar() {
  const [location] = useLocation();
  const { user, logout, isAdmin, canEdit } = useAuth();
  const { projects, ganttItems, roadmapPhases } = useProjects();
  const { collapsed, toggleSidebar } = useSidebarLayout();
  const { unreadCount } = useNotifications();

  const coreProjects = useMemo(() => {
    const source = projects.length === 0 ? PROJECTS : projects;
    const onRoadmap = filterProjectsByMainGantt(source, ganttItems, GANTT_ITEMS);
    const core = onRoadmap.filter((p) => p.group === "core");
    if (projects.length === 0) {
      return core.map(passportToEditable).sort((a, b) => compareSidebarCoreProjects(a, b));
    }
    return [...core].sort((a, b) => compareSidebarCoreProjects(a, b, roadmapPhases));
  }, [projects, ganttItems, roadmapPhases]);

  const supportProjects = useMemo(() => {
    const source = projects.length === 0 ? PROJECTS : projects;
    const onRoadmap = filterProjectsByMainGantt(source, ganttItems, GANTT_ITEMS);
    const sup = onRoadmap.filter((p) => p.group === "support");
    if (projects.length === 0) {
      return sup.map(passportToEditable).sort((a, b) => a.shortTitle.localeCompare(b.shortTitle, "ru"));
    }
    return [...sup].sort((a, b) => a.shortTitle.localeCompare(b.shortTitle, "ru"));
  }, [projects, ganttItems]);

  const navItem = (active: boolean) =>
    cn(
      "sidebar-nav-item mb-1",
      collapsed && "sidebar-nav-item--collapsed",
      active && "active",
    );

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 flex h-screen flex-col overflow-x-hidden print:hidden",
        "w-[var(--app-sidebar-width)] transition-[width] duration-200 ease-out",
      )}
      style={{ background: "var(--sidebar)", borderRight: "1px solid var(--sidebar-border)" }}
    >
      {/* Logo / Header */}
      <div
        className={cn(
          "border-b px-2 py-4",
          !collapsed && "px-4 py-5",
        )}
        style={{ borderColor: "var(--sidebar-border)" }}
      >
        <div className={cn("flex items-center gap-2", collapsed && "flex-col gap-3")}>
          <div
            className={cn(
              "flex items-center gap-2.5",
              collapsed ? "justify-center" : "min-w-0 flex-1",
            )}
          >
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md"
              style={{ background: "var(--nn-blue)" }}
            >
              <Bot className="h-4 w-4 text-white" />
            </div>
            {!collapsed ? (
              <div className="min-w-0">
                <div className="text-sm font-bold leading-tight text-white">Цифровой ДИАК ЗФ</div>
                <div className="text-xs leading-tight" style={{ color: "oklch(0.6 0.04 240)" }}>
                  Норникель · ЗФ
                </div>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={toggleSidebar}
            className={cn(
              "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-white/10 text-white/80 transition-colors hover:bg-white/10 hover:text-white",
              !collapsed && "ml-auto",
            )}
            title={collapsed ? "Развернуть панель" : "Свернуть панель"}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Развернуть боковую панель" : "Свернуть боковую панель"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" strokeWidth={2} /> : <ChevronLeft className="h-4 w-4" strokeWidth={2} />}
          </button>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
        <Link href="/">
          <div
            className={navItem(location === "/")}
            title={collapsed ? "Главная" : undefined}
          >
            <LayoutDashboard className="h-4 w-4 flex-shrink-0" />
            {!collapsed ? <span className="font-medium">Главная</span> : null}
          </div>
        </Link>

        <Link href="/me/tasks">
          <div
            className={navItem(location === "/me/tasks")}
            title={collapsed ? "Мои задачи" : undefined}
          >
            <div className="relative flex-shrink-0">
              <Inbox className="h-4 w-4" />
              {unreadCount > 0 ? (
                <span
                  className="absolute -right-1.5 -top-1.5 inline-flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-rose-500 px-[3px] text-[9px] font-bold leading-none text-white ring-2 ring-[var(--sidebar)]"
                  aria-hidden
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
            </div>
            {!collapsed ? (
              <span className="font-medium flex-1 flex items-center gap-1.5">
                Мои задачи
              </span>
            ) : null}
          </div>
        </Link>

        <Link href="/reports">
          <div
            className={navItem(location === "/reports")}
            title={collapsed ? "Отчётность" : undefined}
          >
            <History className="h-4 w-4 flex-shrink-0" />
            {!collapsed ? <span className="font-medium">Отчётность</span> : null}
          </div>
        </Link>

        <Link href="/memos">
          <div
            className={navItem(location === "/memos")}
            title={collapsed ? "Журнал встреч" : undefined}
          >
            <ScrollText className="h-4 w-4 flex-shrink-0" />
            {!collapsed ? <span className="font-medium">Журнал встреч</span> : null}
          </div>
        </Link>

        {canEdit ? (
          <Link href="/history">
            <div
              className={navItem(location === "/history")}
              title={collapsed ? "История / откат" : undefined}
            >
              <RotateCcw className="h-4 w-4 flex-shrink-0" />
              {!collapsed ? <span className="font-medium">История / откат</span> : null}
            </div>
          </Link>
        ) : null}

        {isAdmin ? (
          <>
            {!collapsed ? (
              <div
                className="px-3 pb-1.5 pt-4 text-xs font-semibold uppercase tracking-wider"
                style={{ color: "oklch(0.45 0.04 240)" }}
              >
                Администрирование
              </div>
            ) : null}
            <Link href="/admin/users">
              <div
                className={navItem(location === "/admin/users")}
                title={collapsed ? "Пользователи" : undefined}
              >
                <UserCog className="h-4 w-4 flex-shrink-0" />
                {!collapsed ? <span className="font-medium">Пользователи</span> : null}
              </div>
            </Link>
            <Link href="/admin/audit">
              <div
                className={navItem(location === "/admin/audit")}
                title={collapsed ? "Журнал аудита" : undefined}
              >
                <ScrollText className="h-4 w-4 flex-shrink-0" />
                {!collapsed ? <span className="font-medium">Журнал аудита</span> : null}
              </div>
            </Link>
          </>
        ) : null}

        {!collapsed ? (
          <div
            className="px-3 pb-1.5 pt-4 text-xs font-semibold uppercase tracking-wider"
            style={{ color: "oklch(0.45 0.04 240)" }}
          >
            Профильные задачи
          </div>
        ) : (
          <div className="mt-2 border-t border-[var(--sidebar-border)] pt-2" aria-hidden />
        )}
        {coreProjects.map((project) => {
          const Icon = iconMap[project.icon] || FileText;
          const isActive = location === `/project/${project.id}`;
          return (
            <Link key={project.id} href={`/project/${project.id}`}>
              <div
                className={cn(navItem(isActive), "group")}
                title={collapsed ? project.shortTitle : undefined}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="h-4 w-4" />
                  <span
                    className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full"
                    style={{ background: statusColors[project.status] }}
                  />
                </div>
                {!collapsed ? (
                  <>
                    <span className="truncate text-sm">{project.shortTitle}</span>
                    {isActive ? <ChevronRight className="ml-auto h-3 w-3 flex-shrink-0" /> : null}
                  </>
                ) : null}
              </div>
            </Link>
          );
        })}

        {!collapsed ? (
          <div
            className="px-3 pb-1.5 pt-4 text-xs font-semibold uppercase tracking-wider"
            style={{ color: "oklch(0.45 0.04 240)" }}
          >
            Непрофильные задачи
          </div>
        ) : (
          <div className="mt-2 border-t border-[var(--sidebar-border)] pt-2" aria-hidden />
        )}
        {supportProjects.map((project) => {
          const Icon = iconMap[project.icon] || FileText;
          const isActive = location === `/project/${project.id}`;
          return (
            <Link key={project.id} href={`/project/${project.id}`}>
              <div
                className={navItem(isActive)}
                title={collapsed ? project.shortTitle : undefined}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="h-4 w-4" />
                  <span
                    className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full"
                    style={{ background: statusColors[project.status] }}
                  />
                </div>
                {!collapsed ? (
                  <>
                    <span className="truncate text-sm">{project.shortTitle}</span>
                    {isActive ? <ChevronRight className="ml-auto h-3 w-3 flex-shrink-0" /> : null}
                  </>
                ) : null}
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div
        className={cn("border-t px-2 py-3 text-xs", !collapsed && "px-4")}
        style={{ borderColor: "var(--sidebar-border)", color: "oklch(0.45 0.04 240)" }}
      >
        {user ? (
          <div className={cn("mb-2", collapsed ? "mb-2" : "")}>
            <NotificationBell collapsed={collapsed} />
          </div>
        ) : null}
        {user ? (
          <div
            className={cn(
              "mb-2 border-b pb-2",
              collapsed && "mb-0 border-0 pb-0",
            )}
            style={{ borderColor: "var(--sidebar-border)" }}
          >
            {!collapsed ? (
              <>
                <div className="truncate font-medium" style={{ color: "oklch(0.7 0.04 240)" }} title={user.login}>
                  {user.login}
                </div>
                <div className="text-[10px] uppercase tracking-wide opacity-80">{user.role}</div>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => logout().catch(() => {})}
              className={cn(
                "mt-2 flex items-center justify-center gap-1.5 rounded-md border border-white/10 px-2 py-1.5 text-[11px] font-medium transition-colors hover:bg-white/5",
                collapsed && "mt-0 w-full px-0",
              )}
              style={{ color: "oklch(0.65 0.04 240)" }}
              title={collapsed ? "Выйти" : undefined}
            >
              <LogOut className="h-3.5 w-3.5 flex-shrink-0" />
              {!collapsed ? "Выйти" : null}
            </button>
          </div>
        ) : null}
        {!collapsed ? (
          <>
            <div className="font-medium" style={{ color: "oklch(0.6 0.04 240)" }}>
              Амбиция к Q2 2027
            </div>
            <div className="mt-0.5">Первый цифровой департамент в РФ</div>
          </>
        ) : null}
      </div>
    </aside>
  );
}
